import React from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buildCompanySavePayload } from '../../utils/settingsForm';
import { getTimeZoneOptions } from '../../utils/timeZoneOptions';
import AutocompleteField from '../inputs/AutocompleteField';
import SettingsSectionCard from './SettingsSectionCard';

function CompanySettingsSection({
  form,
  setField,
  setAddressField,
  onSave,
  onReset,
  loading,
  saving,
  error,
  success,
  setSuccess,
}) {
  const { t } = useTranslation();
  const timeZoneOptions = getTimeZoneOptions();

  const handleSave = () => onSave(buildCompanySavePayload(form));

  if (loading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>;
  }

  return (
    <SettingsSectionCard
      title={t('settings.sections.company')}
      description={t('settings.sections.companyHelp')}
      error={error}
      success={success}
      successMessage={t('settings.saved')}
      onCloseSuccess={() => setSuccess(false)}
    >
      <Stack spacing={2.5}>
        <TextField
          label={t('settings.companyName')}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          size="small"
          fullWidth
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label={t('settings.email')} value={form.email} onChange={(e) => setField('email', e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label={t('settings.phone')} value={form.phone} onChange={(e) => setField('phone', e.target.value)} size="small" fullWidth />
          </Grid>
        </Grid>
        <TypographySection title={t('settings.primaryAddress')} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label={t('clients.address')} value={form.address.street} onChange={(e) => setAddressField('street', e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label={t('clients.city')} value={form.address.city} onChange={(e) => setAddressField('city', e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label={t('settings.state')} value={form.address.state} onChange={(e) => setAddressField('state', e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label={t('settings.zipCode')} value={form.address.zipCode} onChange={(e) => setAddressField('zipCode', e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label={t('clients.country')} value={form.address.country} onChange={(e) => setAddressField('country', e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutocompleteField
              label={t('settings.timeZone')}
              options={timeZoneOptions}
              value={form.timeZone || 'UTC'}
              onChange={(tz) => setField('timeZone', tz || 'UTC')}
              helperText={t('settings.timeZoneHelp')}
              disableClearable
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
          <Button variant="outlined" onClick={onReset} disabled={saving}>{t('common.reset')}</Button>
        </Stack>
      </Stack>
    </SettingsSectionCard>
  );
}

function TypographySection({ title }) {
  return (
    <Box sx={{ pt: 1 }}>
      <Box component="span" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>{title}</Box>
    </Box>
  );
}

export default CompanySettingsSection;
